import {
  Box,
  Checkbox,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Control, Controller, FieldErrors } from "react-hook-form";

import {
  ALLOWED_MODELS_BY_TYPE,
  ISKUForm,
  modelLabelMap,
} from "../variables/skuData";

interface SKUFormItemProps {
  index: number;
  componentName: string; // read-only, from skuCatalog
  componentType: string; // e.g. "AGX", "CH4", "OPT" — used to derive allowed models
  control: Control<ISKUForm>;
  errors: FieldErrors<ISKUForm>;
  nodeCount: number;
  agxNodeNames: string[];
}

const typeColors: Record<
  string,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  AGX: "secondary",
  CH4: "warning",
  OPT: "primary",
  THM: "error",
  VOC: "success",
  LWOP: "success",
  POE: "info",
};

const SKUFormItem = ({
  index,
  componentName,
  componentType,
  control,
  errors,
  nodeCount,
  agxNodeNames,
}: SKUFormItemProps): JSX.Element => {
  const allowedModels = ALLOWED_MODELS_BY_TYPE[componentType] ?? [];
  const isReadOnly = allowedModels.length === 0;
  const showNodeSelector =
    nodeCount > 1 && componentType !== "AGX" && componentType !== "POE";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        pt: 1,
        pb: 3,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 200 }}
      >
        <Chip
          label={componentType}
          size="small"
          color={typeColors[componentType] ?? "default"}
          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
        />
        <Typography variant="body2" fontWeight={500}>
          {componentName}
        </Typography>
      </Box>

      {isReadOnly ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          Processing node — no model assignment
        </Typography>
      ) : (
        <Controller
          name={`skuDataConfigurations.${index}.models`}
          control={control}
          render={({ field }) => (
            <TextField
              select
              size="small"
              label="Model(s)"
              value={field.value ?? []}
              onChange={event =>
                field.onChange(
                  (event.target.value as unknown as string[]).filter(
                    val => val.trim() !== ""
                  )
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: selected =>
                  (selected as string[])
                    .filter(m => m.trim() !== "")
                    .map(m => modelLabelMap[m] || m)
                    .join(", "),
              }}
              sx={{ minWidth: 240, position: "relative" }}
              error={!!errors?.skuDataConfigurations?.[index]?.models}
              helperText={
                " "
                // (field.value ?? []).length === 0
                //   ? "No models — AI services won't be created"
                //   : " "
              }
              FormHelperTextProps={{
                sx: {
                  position: "absolute",
                  bottom: -22,
                  ...((field.value ?? []).length === 0 && {
                    color: "warning.main",
                  }),
                },
              }}
            >
              {allowedModels.map(model => (
                <MenuItem key={model} value={model}>
                  <Checkbox
                    checked={(field.value ?? []).includes(model)}
                    sx={{ marginRight: 1 }}
                  />
                  {modelLabelMap[model] || model}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}

      {showNodeSelector && (
        <Controller
          name={`skuDataConfigurations.${index}.defaultNodeName`}
          control={control}
          render={({ field }) => (
            <TextField
              select
              size="small"
              label="Default Node"
              value={field.value ?? ""}
              onChange={field.onChange}
              sx={{ minWidth: 140 }}
              helperText=" "
              FormHelperTextProps={{
                sx: { position: "absolute", bottom: -22 },
              }}
            >
              {agxNodeNames.map(name => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}
    </Box>
  );
};

export default SKUFormItem;
